import LocalizedLink from '@components/Localization/LocalizedLink'
import { Box, Button, Flex, OldHeading, OldText, Spinner, theme } from '@gassan-ui'
import { useCheckoutAddressesQuery } from '@generated'
import { dataLayerBeginCheckout } from '@lib/data-layer-social-ads/begin-checkout'
import { cartItemsToDataLayerItems } from '@lib/data-layer-social-ads/helpers/cartItemsToDataLayerItems'
import { formatPrice } from '@lib/formatPrice'
import { useCart } from '@lib/hooks/use-cart'
import { useMe } from '@lib/hooks/use-me'
import { motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { rgba } from 'polished'
import { FC } from 'react'
import styled from 'styled-components'
import USPs from '../../USPs'
import Logos from './Logos'

const Parent = styled(motion(Box))``

type Props = {
  status: 'updating' | 'idle'
}

const CartSummary: FC<Props> = ({ status }) => {
  const { t } = useTranslation(['other', 'navigation'])
  const { cart } = useCart()
  const { isLoggedIn } = useMe()
  // Prefetch checkout addresses if logged in, so they show up inmediatly in the next step
  useCheckoutAddressesQuery({ pause: !isLoggedIn })

  return (
    <>
      <Parent
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        p={['1.5rem 1rem', '2.5rem 3rem']}
        bg="pampasLight"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          pb={['1rem', '1.5rem']}
          mb={['1rem', '1.5rem']}
          borderBottom={`1px solid ${rgba(theme.colors.tabaccoBrown, 0.2)}`}
        >
          <OldHeading variant="h5" mb="0">
            {t('shipping', { ns: 'other' })}
          </OldHeading>
          <OldText color="shades.800" mb="0">
            {t('freeInNetherlands', { ns: 'other' })}
          </OldText>
        </Flex>
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          pb={['1rem', '1.5rem']}
          mb={['1rem', '1.5rem']}
          borderBottom={`1px solid ${rgba(theme.colors.tabaccoBrown, 0.2)}`}
        >
          <Box>
            <OldHeading variant="h5" mb="0">
              {t('total', { ns: 'other' })}
            </OldHeading>
            <OldText
              variant="small"
              color={rgba(theme.colors.tabaccoBrown, 0.6)}
              mb="0"
              minHeight={22}
            >
              {cart &&
                (cart.totalTax ? (
                  <>
                    {`${t('including', { ns: 'other' })} ${formatPrice(cart.totalTax)} ${t('vat', {
                      ns: 'other',
                    })} in NL`}
                  </>
                ) : (
                  t('noVatCharged', { ns: 'other' })
                ))}
            </OldText>
          </Box>
          <Box>
            {status === 'updating' ? (
              <Box color="tabaccoBrown">
                <Spinner className="text-xs md:text-2xl" />
              </Box>
            ) : (
              <OldText variant="large" mb="0">
                {cart && formatPrice(cart.totalPriceIncTax, true)}
              </OldText>
            )}
          </Box>
        </Flex>
        <LocalizedLink
          href={
            isLoggedIn
              ? t('checkout.order', { ns: 'navigation' })
              : t('checkout.index', { ns: 'navigation' })
          }
        >
          <Button
            className="w-full"
            variant="dark"
            status={status === 'updating' ? 'disabled' : 'idle'}
            as="link"
            onClick={beginCheckout}
          >
            {t('order', { ns: 'other' })}
          </Button>
        </LocalizedLink>
        <USPs />
        <Logos />
      </Parent>
    </>
  )

  function beginCheckout(): boolean {
    if (cart && cart.items) {
      const items = cartItemsToDataLayerItems(cart.items)
      dataLayerBeginCheckout(items)
    }
    return true
  }
}

export default CartSummary
